import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Sparkles,
  Eye,
  Upload,
  X,
  Link,
  ImageIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ServiceCard } from "@/components/ServiceCard";

interface Service {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  sort_order: number;
  welcome_message: string | null;
  bg_image_url: string | null;
}

export function ServiceManagement() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formActive, setFormActive] = useState(true);
  const [formWelcomeMessage, setFormWelcomeMessage] = useState("");
  const [formBgImageUrl, setFormBgImageUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bgImageMode, setBgImageMode] = useState<"url" | "upload">("url");
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // View state
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    const { data, error } = await supabase
      .from("services")
      .select("*")
      .order("sort_order", { ascending: true });

    if (!error && data) {
      setServices(data as Service[]);
    }
    setLoading(false);
  };

  const openDialog = (service?: Service) => {
    if (service) {
      setEditingService(service);
      setFormName(service.name);
      setFormDescription(service.description || "");
      setFormActive(service.active);
      setFormWelcomeMessage(service.welcome_message || "");
      setFormBgImageUrl(service.bg_image_url || "");
      setBgImageMode(service.bg_image_url?.includes("supabase") ? "upload" : "url");
    } else {
      setEditingService(null);
      setFormName("");
      setFormDescription("");
      setFormActive(true);
      setFormWelcomeMessage("");
      setFormBgImageUrl("");
      setBgImageMode("url");
    }
    setDialogOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 10 Mo");
      return;
    }

    setUploadingImage(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("services")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("services")
        .getPublicUrl(fileName);

      setFormBgImageUrl(publicUrl);
      toast.success("Image téléchargée");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Échec du téléchargement");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveImage = () => {
    setFormBgImageUrl("");
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error("Le nom du service est requis");
      return;
    }

    setSaving(true);

    if (editingService) {
      const { error } = await supabase
        .from("services")
        .update({
          name: formName.trim(),
          description: formDescription.trim() || null,
          active: formActive,
          welcome_message: formWelcomeMessage.trim() || null,
          bg_image_url: formBgImageUrl.trim() || null,
        })
        .eq("id", editingService.id);

      if (error) {
        toast.error("Échec de la mise à jour");
      } else {
        toast.success("Service mis à jour");
        setServices(prev =>
          prev.map(s =>
            s.id === editingService.id
              ? { ...s, name: formName.trim(), description: formDescription.trim() || null, active: formActive, welcome_message: formWelcomeMessage.trim() || null, bg_image_url: formBgImageUrl.trim() || null }
              : s
          )
        );
        setDialogOpen(false);
      }
    } else {
      const maxOrder = services.length > 0 ? Math.max(...services.map(s => s.sort_order)) : 0;
      const { data, error } = await supabase
        .from("services")
        .insert({
          name: formName.trim(),
          description: formDescription.trim() || null,
          active: formActive,
          sort_order: maxOrder + 1,
          welcome_message: formWelcomeMessage.trim() || null,
          bg_image_url: formBgImageUrl.trim() || null,
        })
        .select()
        .single();

      if (error) {
        toast.error("Échec de la création");
      } else {
        toast.success("Service créé");
        setServices(prev => [...prev, data as Service]);
        setDialogOpen(false);
      }
    }

    setSaving(false);
  };

  const handleToggleActive = async (service: Service) => {
    const { error } = await supabase
      .from("services")
      .update({ active: !service.active })
      .eq("id", service.id);

    if (error) {
      toast.error("Échec de la mise à jour");
    } else {
      setServices(prev =>
        prev.map(s => (s.id === service.id ? { ...s, active: !s.active } : s))
      );
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce service ?")) return;

    const { error } = await supabase.from("services").delete().eq("id", id);

    if (error) {
      toast.error("Échec de la suppression");
    } else {
      toast.success("Service supprimé");
      setServices(prev => prev.filter(s => s.id !== id));
    }
  };

  const moveService = async (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= services.length) return;

    const newServices = [...services];
    [newServices[index], newServices[newIndex]] = [newServices[newIndex], newServices[index]];

    const updates = newServices.map((s, i) => ({ id: s.id, sort_order: i }));
    
    for (const update of updates) {
      await supabase
        .from("services")
        .update({ sort_order: update.sort_order })
        .eq("id", update.id);
    }

    setServices(newServices.map((s, i) => ({ ...s, sort_order: i })));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {services.length} service{services.length > 1 ? "s" : ""} configuré{services.length > 1 ? "s" : ""}
        </p>
        <div className="flex items-center gap-2">
          <Button 
            variant={showPreview ? "default" : "outline"} 
            size="sm" 
            onClick={() => setShowPreview(!showPreview)}
          >
            <Eye size={16} className="mr-1" />
            Aperçu
          </Button>
          <Button variant="gold" size="sm" onClick={() => openDialog()}>
            <Plus size={16} className="mr-1" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Preview Mode */}
      {showPreview && (
        <div className="bg-background rounded-xl border border-border p-4 space-y-3">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-3">Aperçu client</p>
          {services.filter(s => s.active).map(service => (
            <ServiceCard
              key={service.id}
              name={service.name}
              description={service.description}
              bgImageUrl={service.bg_image_url}
              interactive={false}
            />
          ))}
          {services.filter(s => s.active).length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">Aucun service actif à afficher</p>
          )}
        </div>
      )}

      {/* Services List */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {services.length === 0 ? (
          <div className="p-8 text-center">
            <Sparkles size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Aucun service configuré</p>
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => openDialog()}>
              Créer un service
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {services.map((service, index) => (
              <div
                key={service.id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors"
              >
                {/* Drag handle / reorder */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveService(index, "up")}
                    disabled={index === 0}
                    className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
                      <path d="M5 0L10 6H0L5 0Z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => moveService(index, "down")}
                    disabled={index === services.length - 1}
                    className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
                      <path d="M5 6L0 0H10L5 6Z" />
                    </svg>
                  </button>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground text-sm">{service.name}</p>
                  {service.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {service.description}
                    </p>
                  )}
                </div>

                {/* Status */}
                <Switch
                  checked={service.active}
                  onCheckedChange={() => handleToggleActive(service)}
                />

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openDialog(service)}
                  >
                    <Edit size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(service.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingService ? "Modifier le service" : "Nouveau service"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nom du service</Label>
              <Input
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="Ex: Billets d'avion"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formDescription}
                onChange={e => setFormDescription(e.target.value)}
                placeholder="Ex: Réservation de vols, upgrades, lounges VIP"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Message de démarrage</Label>
              <Textarea
                value={formWelcomeMessage}
                onChange={e => setFormWelcomeMessage(e.target.value)}
                placeholder="Ex: Bonjour, j'aimerais réserver un billet d'avion..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Message pré-rempli que le client peut envoyer pour démarrer la conversation
              </p>
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <ImageIcon size={14} />
                Image de fond
              </Label>
              
              <Tabs value={bgImageMode} onValueChange={(v) => setBgImageMode(v as "url" | "upload")}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="url" className="text-xs">
                    <Link size={12} className="mr-1" />
                    URL
                  </TabsTrigger>
                  <TabsTrigger value="upload" className="text-xs">
                    <Upload size={12} className="mr-1" />
                    Télécharger
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="url" className="mt-2">
                  <Input
                    value={formBgImageUrl}
                    onChange={e => setFormBgImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </TabsContent>
                
                <TabsContent value="upload" className="mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        Téléchargement...
                      </>
                    ) : (
                      <>
                        <Upload size={14} className="mr-2" />
                        Choisir une image (max 10 Mo)
                      </>
                    )}
                  </Button>
                </TabsContent>
              </Tabs>

              {formBgImageUrl && (
                <div className="relative rounded-lg overflow-hidden border border-border">
                  <img 
                    src={formBgImageUrl} 
                    alt="Aperçu" 
                    className="w-full h-24 object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23ddd' width='100' height='100'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' text-anchor='middle' dy='.3em' font-size='12'%3EImage invalide%3C/text%3E%3C/svg%3E";
                    }}
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-6 w-6"
                    onClick={handleRemoveImage}
                  >
                    <X size={12} />
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label>Service actif</Label>
              <Switch checked={formActive} onCheckedChange={setFormActive} />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button
                variant="gold"
                className="flex-1"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  "Enregistrer"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
